import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  applyKeyframeEase,
  evaluateKeyframeSpeed,
  isScaleLinked,
  keyIncomingAbsSpeed,
  keyOutgoingAbsSpeed,
  resolveSegmentBezier,
  setIncomingAbsSpeed,
  setIncomingHandle,
  setOutgoingAbsSpeed,
  setOutgoingHandle,
  syncLinkedScaleKeyframes,
  type AnimatableProperty,
  type Keyframe,
  type KeyframeInterpolation,
} from '@cinegrid/core';
import { useEditorStore } from '../../state/editorStore';

const GRAPH_PROPS_BASE: { id: AnimatableProperty; label: string }[] = [
  { id: 'positionX', label: 'Position X' },
  { id: 'positionY', label: 'Position Y' },
  { id: 'scaleX', label: 'Scale X' },
  { id: 'scaleY', label: 'Scale Y' },
  { id: 'rotation', label: 'Rotation' },
  { id: 'opacity', label: 'Opacity' },
  { id: 'anchorX', label: 'Anchor X' },
  { id: 'anchorY', label: 'Anchor Y' },
];

function graphPropsForClip(scaleLinked: boolean): { id: AnimatableProperty; label: string }[] {
  if (!scaleLinked) return GRAPH_PROPS_BASE;
  return GRAPH_PROPS_BASE.filter((p) => p.id !== 'scaleY').map((p) =>
    p.id === 'scaleX' ? { id: 'scaleX', label: 'Scale' } : p
  );
}

const EASE_PRESETS: {
  id: KeyframeInterpolation;
  label: string;
  title: string;
}[] = [
  { id: 'linear', label: 'Linear', title: 'Linear' },
  { id: 'easeIn', label: 'Ease In', title: 'Ease In' },
  { id: 'easeOut', label: 'Ease Out', title: 'Ease Out' },
  { id: 'easeInOut', label: 'Easy Ease', title: 'Easy Ease (in & out)' },
  { id: 'hold', label: 'Hold', title: 'Hold / step' },
];

function TangentIcon({ mode }: { mode: KeyframeInterpolation }) {
  const common = {
    width: 22,
    height: 14,
    viewBox: '0 0 22 14',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.4,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (mode) {
    case 'linear':
      return (
        <svg {...common} aria-hidden>
          <path d="M2 12 L20 2" />
          <circle cx="2" cy="12" r="1.6" fill="currentColor" stroke="none" />
          <circle cx="20" cy="2" r="1.6" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'easeIn':
      return (
        <svg {...common} aria-hidden>
          <path d="M2 12 C8 12 14 10 20 2" />
          <circle cx="2" cy="12" r="1.6" fill="currentColor" stroke="none" />
          <circle cx="20" cy="2" r="1.6" fill="currentColor" stroke="none" />
          <path d="M2 12 H8" opacity="0.7" />
        </svg>
      );
    case 'easeOut':
      return (
        <svg {...common} aria-hidden>
          <path d="M2 12 C8 4 14 2 20 2" />
          <circle cx="2" cy="12" r="1.6" fill="currentColor" stroke="none" />
          <circle cx="20" cy="2" r="1.6" fill="currentColor" stroke="none" />
          <path d="M14 2 H20" opacity="0.7" />
        </svg>
      );
    case 'easeInOut':
      return (
        <svg {...common} aria-hidden>
          <path d="M2 12 C6 12 8 2 11 2 C14 2 16 12 20 12" />
          <circle cx="2" cy="12" r="1.6" fill="currentColor" stroke="none" />
          <circle cx="20" cy="12" r="1.6" fill="currentColor" stroke="none" />
          <path d="M2 12 H6M16 12 H20" opacity="0.7" />
        </svg>
      );
    case 'hold':
      return (
        <svg {...common} aria-hidden>
          <path d="M2 12 H11 V2 H20" />
          <circle cx="2" cy="12" r="1.6" fill="currentColor" stroke="none" />
          <circle cx="20" cy="2" r="1.6" fill="currentColor" stroke="none" />
        </svg>
      );
  }
}

function speedUnit(prop: AnimatableProperty): string {
  if (prop === 'opacity') return '/s';
  if (prop === 'rotation') return '°/s';
  if (prop.startsWith('scale')) return '/s';
  return 'px/s';
}

/** Display speed at a key: prefer outgoing, else incoming. */
function keyDisplaySpeed(kfs: Keyframe[], index: number): number {
  const k = kfs[index]!;
  if (k.interpolation === 'hold') return 0;
  if (index < kfs.length - 1) return keyOutgoingAbsSpeed(k, kfs[index + 1]!);
  if (index > 0) return keyIncomingAbsSpeed(kfs[index - 1]!, k);
  return 0;
}

/** After Effects–style speed graph (time × |speed|). */
export function KeyGraphPanel() {
  const selection = useEditorStore((s) => s.selection);
  const project = useEditorStore((s) => s.project);
  const playhead = useEditorStore((s) => s.playhead);
  const setPlayhead = useEditorStore((s) => s.setPlayhead);
  const setPlaying = useEditorStore((s) => s.setPlaying);
  const updateClipProperties = useEditorStore((s) => s.updateClipProperties);

  const isPlaying = useEditorStore((s) => s.isPlaying);
  const [plotWidth, setPlotWidth] = useState(600);
  /** Graph-local zoom (px/sec) — independent of timeline so keys stay findable. */
  const [graphZoom, setGraphZoom] = useState(120);
  /** Center of the visible time window (seconds, clip-local). */
  const [viewCenter, setViewCenter] = useState(0);
  const viewCenterRef = useRef(0);
  viewCenterRef.current = viewCenter;
  const graphZoomRef = useRef(graphZoom);
  graphZoomRef.current = graphZoom;
  const plotWidthRef = useRef(plotWidth);
  plotWidthRef.current = plotWidth;

  const clip = useMemo(
    () => project.clips.find((c) => c.id === selection[0]),
    [project.clips, selection]
  );

  const scaleLinked = isScaleLinked(clip?.properties);
  const graphProps = useMemo(() => graphPropsForClip(scaleLinked), [scaleLinked]);

  // Follow playhead while playing so the graph scrolls with time
  useEffect(() => {
    if (!clip || !isPlaying) return;
    const local = Math.max(0, playhead - clip.start);
    setViewCenter(local);
  }, [clip, isPlaying, playhead]);

  const [property, setProperty] = useState<AnimatableProperty>('positionX');
  // When scale is linked, Scale Y row is hidden — keep selection on Scale (scaleX)
  useEffect(() => {
    if (scaleLinked && property === 'scaleY') setProperty('scaleX');
  }, [scaleLinked, property]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [marquee, setMarquee] = useState<{
    x0: number;
    y0: number;
    x1: number;
    y1: number;
  } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const kfsRef = useRef<Keyframe[]>([]);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origins: { id: string; time: number; speed: number }[];
  } | null>(null);
  const scrubRef = useRef(false);
  const tangentDragRef = useRef<{
    keyId: string;
    which: 'in' | 'out';
    aId: string;
    bId: string;
    mode: 'influence' | 'speed';
  } | null>(null);
  const marqueeRef = useRef<{
    x0: number;
    y0: number;
    x1: number;
    y1: number;
    additive: boolean;
    baseIds: string[];
    moved: boolean;
  } | null>(null);
  const layoutRef = useRef({
    padL: 48,
    padR: 12,
    padT: 16,
    padB: 24,
    t0: 0,
    t1: 1,
    s0: 0,
    s1: 1,
    w: 1,
    h: 1,
  });

  const kfs = useMemo(() => {
    if (!clip) return [] as Keyframe[];
    return clip.properties.keyframes
      .filter((k) => k.property === property)
      .sort((a, b) => a.time - b.time);
  }, [clip, property]);
  kfsRef.current = kfs;

  useEffect(() => {
    setSelectedIds((ids) => ids.filter((id) => kfs.some((k) => k.id === id)));
  }, [kfs]);

  const fitToKeys = useCallback(() => {
    const clipId = clip?.id;
    if (!clipId) return;
    const live = useEditorStore.getState().project.clips.find((c) => c.id === clipId);
    if (!live) return;
    const width = Math.max(120, plotWidthRef.current - 48 - 12);
    const ph = useEditorStore.getState().playhead;
    const localPh = Math.max(0, ph - live.start);
    const propKeys = live.properties.keyframes
      .filter((k) => k.property === property)
      .map((k) => k.time);
    // Prefer live keys; fall back to current prop filter ref
    const times = propKeys.length ? propKeys : kfsRef.current.map((k) => k.time);
    let tMin: number;
    let tMax: number;
    if (times.length === 0) {
      tMin = 0;
      tMax = Math.max(live.duration || 1, localPh, 1);
    } else {
      tMin = Math.min(...times, localPh);
      tMax = Math.max(...times, localPh);
      if (tMax - tMin < 1e-3) {
        tMin = Math.max(0, tMin - 0.25);
        tMax = tMin + 0.5;
      }
    }
    const pad = Math.max(0.08, (tMax - tMin) * 0.12);
    tMin = Math.max(0, tMin - pad);
    tMax = tMax + pad;
    const span = Math.max(0.35, tMax - tMin);
    const nextZoom = Math.min(800, Math.max(8, width / span));
    const center = (tMin + tMax) / 2;
    setGraphZoom(nextZoom);
    setViewCenter(center);
    viewCenterRef.current = center;
    graphZoomRef.current = nextZoom;
  }, [clip?.id, property]);

  // Fit when clip or property changes so keys are always in view
  useEffect(() => {
    if (!clip?.id) return;
    const t = window.setTimeout(() => fitToKeys(), 0);
    return () => window.clearTimeout(t);
  }, [clip?.id, property, fitToKeys]);

  // Fit once when keys first appear for this property
  const prevKeyCountRef = useRef(0);
  useEffect(() => {
    const n = kfs.length;
    if (prevKeyCountRef.current === 0 && n > 0) fitToKeys();
    prevKeyCountRef.current = n;
  }, [kfs.length, fitToKeys]);

  const timeRange = useMemo(() => {
    const visible = Math.max(0.35, (plotWidth || 600) / Math.max(8, graphZoom));
    const half = visible / 2;
    return { min: viewCenter - half, max: viewCenter + half };
  }, [viewCenter, plotWidth, graphZoom]);

  const speedRange = useMemo(() => {
    if (!clip || kfs.length === 0) return { min: 0, max: 100 };
    let max = 1;
    const steps = 64;
    const tLo = timeRange.min;
    const tHi = timeRange.max;
    if (kfs.length >= 2) {
      for (let i = 0; i < kfs.length - 1; i++) {
        const a = kfs[i]!;
        const b = kfs[i + 1]!;
        if (b.time < tLo || a.time > tHi) continue;
        for (let s = 0; s <= steps; s++) {
          const t = a.time + ((b.time - a.time) * s) / steps;
          if (t < tLo || t > tHi) continue;
          max = Math.max(max, evaluateKeyframeSpeed(clip.properties.keyframes, property, t));
        }
      }
    }
    for (let i = 0; i < kfs.length; i++) {
      max = Math.max(max, keyDisplaySpeed(kfs, i));
    }
    return { min: 0, max: Math.max(1, max * 1.15) };
  }, [clip, kfs, property, timeRange.max, timeRange.min]);

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const cssW = wrap.clientWidth || 400;
    const cssH = wrap.clientHeight || 200;
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const padL = 48;
    const padR = 12;
    const padT = 18;
    const padB = 28;
    const w = cssW;
    const h = cssH;
    const plotW = w - padL - padR;
    const plotH = h - padT - padB;
    const { min: s0, max: s1 } = speedRange;
    const { min: t0, max: t1 } = timeRange;

    layoutRef.current = { padL, padR, padT, padB, t0, t1, s0, s1, w, h };

    const xOf = (t: number) => padL + ((t - t0) / (t1 - t0)) * plotW;
    const yOf = (s: number) => padT + (1 - (s - s0) / (s1 - s0 || 1)) * plotH;

    const css = getComputedStyle(document.documentElement);
    const bg = css.getPropertyValue('--cg-bg-base').trim() || '#15171a';
    const hair = css.getPropertyValue('--cg-border').trim() || '#2a2e33';
    const textLow = css.getPropertyValue('--cg-text-low').trim() || '#8b9199';
    const accent = css.getPropertyValue('--cg-accent').trim() || '#e8a317';
    const playheadC = css.getPropertyValue('--cg-playhead').trim() || '#5b9fd4';
    const high = css.getPropertyValue('--cg-text-high').trim() || '#edeeef';

    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    // Grid + speed labels
    ctx.strokeStyle = hair;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.55;
    const vSteps = 6;
    for (let i = 0; i <= vSteps; i++) {
      const s = s0 + ((s1 - s0) * i) / vSteps;
      const y = yOf(s);
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(w - padR, y);
      ctx.stroke();
      ctx.fillStyle = textLow;
      ctx.globalAlpha = 0.9;
      ctx.font = '10px ui-sans-serif, system-ui, sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      const label = s >= 100 ? String(Math.round(s)) : s >= 10 ? s.toFixed(0) : s.toFixed(1);
      ctx.fillText(label, padL - 6, y);
      ctx.globalAlpha = 0.55;
    }
    const tSteps = 8;
    for (let i = 0; i <= tSteps; i++) {
      const t = t0 + ((t1 - t0) * i) / tSteps;
      const x = xOf(t);
      ctx.beginPath();
      ctx.moveTo(x, padT);
      ctx.lineTo(x, h - padB);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // Zero baseline
    ctx.strokeStyle = hair;
    ctx.globalAlpha = 0.9;
    ctx.beginPath();
    ctx.moveTo(padL, yOf(0));
    ctx.lineTo(w - padR, yOf(0));
    ctx.stroke();
    ctx.globalAlpha = 1;

    // Speed curve
    if (clip && kfs.length > 1) {
      ctx.beginPath();
      const samples = Math.max(120, Math.floor(plotW * 1.5));
      let started = false;
      for (let i = 0; i <= samples; i++) {
        const t = t0 + ((t1 - t0) * i) / samples;
        const spd = evaluateKeyframeSpeed(clip.properties.keyframes, property, t);
        const x = xOf(t);
        const y = yOf(spd);
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = high;
      ctx.lineWidth = 1.75;
      ctx.stroke();
    }

    // Playhead (grab the line / caret to scrub — empty area does not jump)
    if (clip) {
      const local = playhead - clip.start;
      if (local >= t0 - 1e-6 && local <= t1 + 1e-6) {
        const x = xOf(local);
        ctx.strokeStyle = playheadC;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x, padT);
        ctx.lineTo(x, h - padB);
        ctx.stroke();
        // Top caret handle
        ctx.fillStyle = playheadC;
        ctx.beginPath();
        ctx.moveTo(x, padT + 1);
        ctx.lineTo(x - 6, padT - 7);
        ctx.lineTo(x + 6, padT - 7);
        ctx.closePath();
        ctx.fill();
        // Bottom tick
        ctx.beginPath();
        ctx.moveTo(x, h - padB);
        ctx.lineTo(x - 4, h - padB + 5);
        ctx.lineTo(x + 4, h - padB + 5);
        ctx.closePath();
        ctx.fill();
      }
    }

    // Keys + horizontal influence handles (speed-graph style)
    const showAll = selectedIds.length === 0;
    for (let i = 0; i < kfs.length; i++) {
      const k = kfs[i]!;
      const selected = selectedIds.includes(k.id);
      const showHandles = selected || showAll;
      const spd = keyDisplaySpeed(kfs, i);
      const x = xOf(k.time);
      const y = yOf(spd);

      if (showHandles && k.interpolation !== 'hold') {
        ctx.strokeStyle = accent;
        ctx.lineWidth = 1.25;
        ctx.globalAlpha = selected ? 1 : 0.45;

        // Out influence handle (horizontal at outgoing speed)
        if (i < kfs.length - 1) {
          const next = kfs[i + 1]!;
          const outSpd = keyOutgoingAbsSpeed(k, next);
          const { x1 } = resolveSegmentBezier(k, next);
          const hx = xOf(k.time + x1 * (next.time - k.time));
          const hy = yOf(outSpd);
          ctx.beginPath();
          ctx.moveTo(x, yOf(outSpd));
          ctx.lineTo(hx, hy);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(hx, hy, selected ? 3.5 : 3, 0, Math.PI * 2);
          ctx.fillStyle = accent;
          ctx.fill();
        }

        // In influence handle
        if (i > 0) {
          const prev = kfs[i - 1]!;
          const inSpd = keyIncomingAbsSpeed(prev, k);
          const { x2 } = resolveSegmentBezier(prev, k);
          const hx = xOf(prev.time + x2 * (k.time - prev.time));
          const hy = yOf(inSpd);
          ctx.beginPath();
          ctx.moveTo(x, yOf(inSpd));
          ctx.lineTo(hx, hy);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(hx, hy, selected ? 3.5 : 3, 0, Math.PI * 2);
          ctx.fillStyle = accent;
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }

      const s = selected ? 6 : 5;
      ctx.beginPath();
      ctx.moveTo(x, y - s);
      ctx.lineTo(x + s, y);
      ctx.lineTo(x, y + s);
      ctx.lineTo(x - s, y);
      ctx.closePath();
      ctx.fillStyle = accent;
      ctx.fill();
      if (selected) {
        ctx.strokeStyle = high;
        ctx.lineWidth = 1.25;
        ctx.stroke();
      }
    }

    ctx.fillStyle = textLow;
    ctx.font = '10px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(`sec · ${speedUnit(property)}`, padL, h - 6);

    if (marquee) {
      const rx = Math.min(marquee.x0, marquee.x1);
      const ry = Math.min(marquee.y0, marquee.y1);
      const rw = Math.abs(marquee.x1 - marquee.x0);
      const rh = Math.abs(marquee.y1 - marquee.y0);
      ctx.fillStyle = accent;
      ctx.globalAlpha = 0.12;
      ctx.fillRect(rx, ry, rw, rh);
      ctx.globalAlpha = 1;
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.strokeRect(rx + 0.5, ry + 0.5, rw, rh);
      ctx.setLineDash([]);
    }
  }, [clip, kfs, marquee, playhead, property, selectedIds, speedRange, timeRange]);

  useEffect(() => {
    paint();
    const wrap = wrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => {
      setPlotWidth(wrap.clientWidth || 600);
      paint();
    });
    setPlotWidth(wrap.clientWidth || 600);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [paint]);

  // Wheel: Alt/Cmd = zoom graph; otherwise pan time
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const onWheel = (e: WheelEvent) => {
      if (e.altKey || e.metaKey) {
        e.preventDefault();
        const factor = e.deltaY > 0 ? 0.9 : 1.1;
        setGraphZoom((z) => Math.min(800, Math.max(8, z * factor)));
        return;
      }

      const panPx =
        Math.abs(e.deltaX) > Math.abs(e.deltaY)
          ? e.deltaX
          : e.shiftKey
            ? e.deltaY
            : e.deltaY;
      if (panPx === 0) return;
      e.preventDefault();
      const z = graphZoomRef.current;
      const dt = panPx / Math.max(8, z);
      setViewCenter((c) => c + dt);
    };
    wrap.addEventListener('wheel', onWheel, { passive: false });
    return () => wrap.removeEventListener('wheel', onWheel);
  }, []);

  const canvasPoint = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const screenToGraph = (clientX: number, clientY: number) => {
    const pt = canvasPoint(clientX, clientY);
    const L = layoutRef.current;
    const plotW = L.w - L.padL - L.padR;
    const plotH = L.h - L.padT - L.padB;
    const time = L.t0 + ((pt.x - L.padL) / plotW) * (L.t1 - L.t0);
    const speed = L.s0 + (1 - (pt.y - L.padT) / plotH) * (L.s1 - L.s0);
    return { time, speed: Math.max(0, speed) };
  };

  const hitTestKey = (clientX: number, clientY: number): Keyframe | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const pt = canvasPoint(clientX, clientY);
    const L = layoutRef.current;
    const plotW = L.w - L.padL - L.padR;
    const plotH = L.h - L.padT - L.padB;
    const xOf = (t: number) => L.padL + ((t - L.t0) / (L.t1 - L.t0)) * plotW;
    const yOf = (s: number) => L.padT + (1 - (s - L.s0) / (L.s1 - L.s0 || 1)) * plotH;
    let best: Keyframe | null = null;
    let bestD = 12;
    for (let i = 0; i < kfs.length; i++) {
      const k = kfs[i]!;
      const spd = keyDisplaySpeed(kfs, i);
      const d = Math.hypot(xOf(k.time) - pt.x, yOf(spd) - pt.y);
      if (d < bestD) {
        bestD = d;
        best = k;
      }
    }
    return best;
  };

  const hitTestTangent = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const pt = canvasPoint(clientX, clientY);
    const L = layoutRef.current;
    const plotW = L.w - L.padL - L.padR;
    const plotH = L.h - L.padT - L.padB;
    const xOf = (t: number) => L.padL + ((t - L.t0) / (L.t1 - L.t0)) * plotW;
    const yOf = (s: number) => L.padT + (1 - (s - L.s0) / (L.s1 - L.s0 || 1)) * plotH;
    const showAll = selectedIdsRef.current.length === 0;
    let best: {
      keyId: string;
      which: 'in' | 'out';
      aId: string;
      bId: string;
    } | null = null;
    let bestD = 10;

    for (let i = 0; i < kfs.length; i++) {
      const k = kfs[i]!;
      if (k.interpolation === 'hold') continue;
      if (!showAll && !selectedIdsRef.current.includes(k.id)) continue;

      if (i < kfs.length - 1) {
        const next = kfs[i + 1]!;
        const { x1 } = resolveSegmentBezier(k, next);
        const hx = xOf(k.time + x1 * (next.time - k.time));
        const hy = yOf(keyOutgoingAbsSpeed(k, next));
        const d = Math.hypot(hx - pt.x, hy - pt.y);
        if (d < bestD) {
          bestD = d;
          best = { keyId: k.id, which: 'out', aId: k.id, bId: next.id };
        }
      }
      if (i > 0) {
        const prev = kfs[i - 1]!;
        const { x2 } = resolveSegmentBezier(prev, k);
        const hx = xOf(prev.time + x2 * (k.time - prev.time));
        const hy = yOf(keyIncomingAbsSpeed(prev, k));
        const d = Math.hypot(hx - pt.x, hy - pt.y);
        if (d < bestD) {
          bestD = d;
          best = { keyId: k.id, which: 'in', aId: prev.id, bId: k.id };
        }
      }
    }
    return best;
  };

  const hitTestPlayhead = (clientX: number, clientY: number) => {
    if (!clip) return false;
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const pt = canvasPoint(clientX, clientY);
    const L = layoutRef.current;
    const plotW = L.w - L.padL - L.padR;
    const local = playhead - clip.start;
    const x = L.padL + ((local - L.t0) / (L.t1 - L.t0 || 1)) * plotW;
    // Full plot height + caret handles above/below
    if (pt.y < L.padT - 10 || pt.y > L.h - L.padB + 10) return false;
    return Math.abs(pt.x - x) <= 8;
  };

  const scrubToClientX = (clientX: number) => {
    if (!clip) return;
    const g = screenToGraph(clientX, 0);
    const local = Math.max(0, Math.min(clip.duration || 1e6, g.time));
    setPlaying(false);
    setPlayhead(clip.start + local);
    const z = graphZoomRef.current;
    const visible = Math.max(0.35, (plotWidthRef.current || 600) / Math.max(8, z));
    const half = visible / 2;
    const c = viewCenterRef.current;
    const min = c - half;
    const max = c + half;
    const margin = (max - min) * 0.08;
    if (local < min + margin || local > max - margin) {
      setViewCenter(local);
    }
  };

  const keysInMarquee = (box: { x0: number; y0: number; x1: number; y1: number }) => {
    const L = layoutRef.current;
    const plotW = L.w - L.padL - L.padR;
    const plotH = L.h - L.padT - L.padB;
    const xOf = (t: number) => L.padL + ((t - L.t0) / (L.t1 - L.t0)) * plotW;
    const yOf = (s: number) => L.padT + (1 - (s - L.s0) / (L.s1 - L.s0 || 1)) * plotH;
    const left = Math.min(box.x0, box.x1);
    const right = Math.max(box.x0, box.x1);
    const top = Math.min(box.y0, box.y1);
    const bottom = Math.max(box.y0, box.y1);
    const ids: string[] = [];
    for (let i = 0; i < kfsRef.current.length; i++) {
      const k = kfsRef.current[i]!;
      const x = xOf(k.time);
      const y = yOf(keyDisplaySpeed(kfsRef.current, i));
      if (x >= left && x <= right && y >= top && y <= bottom) ids.push(k.id);
    }
    return ids;
  };

  const applyEase = (mode: KeyframeInterpolation) => {
    if (!clip || selectedIds.length === 0) return;
    let next = clip.properties.keyframes.map((k) =>
      selectedIds.includes(k.id) ? applyKeyframeEase(k, mode) : k
    );
    if (scaleLinked && property === 'scaleX') {
      next = syncLinkedScaleKeyframes(next);
    }
    updateClipProperties(clip.id, { keyframes: next });
  };

  const patchKeyframes = (mapFn: (k: Keyframe) => Keyframe) => {
    if (!clip) return;
    let next = clip.properties.keyframes.map(mapFn);
    if (scaleLinked && property === 'scaleX') {
      next = syncLinkedScaleKeyframes(next);
    }
    updateClipProperties(clip.id, { keyframes: next });
  };

  if (!clip) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-2xs text-text-low">
        Select a clip with keyframes to edit the speed graph.
      </div>
    );
  }

  const localPlayhead = Math.max(0, playhead - clip.start);

  return (
    <div className="flex h-full min-h-0 flex-col bg-panel">
      <div className="flex shrink-0 items-center gap-1 border-b border-hairline px-2 py-1">
        <span className="mr-1 text-2xs text-text-low">Ease</span>
        {EASE_PRESETS.map((p) => {
          const active =
            selectedIds.length > 0 &&
            selectedIds.every(
              (id) => kfs.find((k) => k.id === id)?.interpolation === p.id
            );
          return (
            <button
              key={p.id}
              type="button"
              title={
                selectedIds.length
                  ? `Apply ${p.title} to selected`
                  : `Select a keyframe, then apply ${p.title}`
              }
              disabled={selectedIds.length === 0}
              onClick={() => applyEase(p.id)}
              className={`inline-flex flex-col items-center gap-0.5 border px-1.5 py-1 text-[9px] disabled:opacity-35 ${
                active
                  ? 'border-accent bg-accent/15 text-accent'
                  : 'border-hairline text-text-low hover:border-[var(--cg-border-strong)] hover:text-text-high'
              }`}
            >
              <TangentIcon mode={p.id} />
              <span>{p.label}</span>
            </button>
          );
        })}
        <span className="ml-auto truncate text-2xs text-text-low">
          Speed · {clip.name} · t={localPlayhead.toFixed(2)}s
        </span>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="flex w-[120px] shrink-0 flex-col overflow-auto border-r border-hairline">
          {graphProps.map((p) => {
            const count = clip.properties.keyframes.filter((k) => k.property === p.id).length;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setProperty(p.id);
                  setSelectedIds([]);
                }}
                className={`flex items-center justify-between gap-1 border-b border-hairline px-2 py-1.5 text-left text-2xs ${
                  property === p.id
                    ? 'bg-raised text-accent'
                    : 'text-text-low hover:bg-raised/50 hover:text-text-high'
                }`}
              >
                <span className="truncate">{p.label}</span>
                {count > 0 && (
                  <span className="text-[9px] text-text-low">{count}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div ref={wrapRef} className="relative min-h-0 flex-1">
            <canvas
              ref={canvasRef}
              className="absolute inset-0 h-full w-full cursor-default"
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                const additive = e.shiftKey || e.metaKey || e.ctrlKey;

                const tan = hitTestTangent(e.clientX, e.clientY);
                if (tan) {
                  setSelectedIds([tan.keyId]);
                  tangentDragRef.current = { ...tan, mode: 'influence' };
                  dragRef.current = null;
                  scrubRef.current = false;
                  marqueeRef.current = null;
                  setMarquee(null);
                  (e.target as HTMLElement).setPointerCapture(e.pointerId);
                  return;
                }

                const hit = hitTestKey(e.clientX, e.clientY);
                if (hit) {
                  let nextSelected: string[];
                  if (additive) {
                    nextSelected = selectedIdsRef.current.includes(hit.id)
                      ? selectedIdsRef.current.filter((id) => id !== hit.id)
                      : [...selectedIdsRef.current, hit.id];
                    setSelectedIds(nextSelected);
                    if (!nextSelected.includes(hit.id)) return;
                  } else {
                    nextSelected = selectedIdsRef.current.includes(hit.id)
                      ? selectedIdsRef.current
                      : [hit.id];
                    setSelectedIds(nextSelected);
                  }

                  const origins = kfsRef.current
                    .map((k, i) =>
                      nextSelected.includes(k.id)
                        ? { id: k.id, time: k.time, speed: keyDisplaySpeed(kfsRef.current, i) }
                        : null
                    )
                    .filter(Boolean) as { id: string; time: number; speed: number }[];
                  if (origins.length === 0) return;
                  dragRef.current = {
                    startX: e.clientX,
                    startY: e.clientY,
                    origins,
                  };
                  tangentDragRef.current = null;
                  scrubRef.current = false;
                  marqueeRef.current = null;
                  setMarquee(null);
                  (e.target as HTMLElement).setPointerCapture(e.pointerId);
                  return;
                }

                // Scrub only when grabbing the playhead marker
                if (hitTestPlayhead(e.clientX, e.clientY)) {
                  scrubRef.current = true;
                  dragRef.current = null;
                  tangentDragRef.current = null;
                  marqueeRef.current = null;
                  setMarquee(null);
                  scrubToClientX(e.clientX);
                  (e.target as HTMLElement).setPointerCapture(e.pointerId);
                  return;
                }

                // Empty area → marquee select (Shift/Cmd adds to selection)
                const pt = canvasPoint(e.clientX, e.clientY);
                marqueeRef.current = {
                  x0: pt.x,
                  y0: pt.y,
                  x1: pt.x,
                  y1: pt.y,
                  additive,
                  baseIds: additive ? [...selectedIdsRef.current] : [],
                  moved: false,
                };
                dragRef.current = null;
                tangentDragRef.current = null;
                scrubRef.current = false;
                if (!additive) setSelectedIds([]);
                setMarquee({ x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y });
                (e.target as HTMLElement).setPointerCapture(e.pointerId);
              }}
              onPointerMove={(e) => {
                if (scrubRef.current) {
                  scrubToClientX(e.clientX);
                  return;
                }

                const tan = tangentDragRef.current;
                if (tan) {
                  const a = kfsRef.current.find((k) => k.id === tan.aId);
                  const b = kfsRef.current.find((k) => k.id === tan.bId);
                  if (!a || !b) return;
                  const g = screenToGraph(e.clientX, e.clientY);
                  const D = b.time - a.time || 1e-6;

                  // Free 2D handle: influence (X) + speed (Y) together
                  if (tan.which === 'out') {
                    const influence = clamp01((g.time - a.time) / D);
                    patchKeyframes((k) =>
                      k.id === a.id ? setOutgoingHandle(a, b, influence, g.speed) : k
                    );
                  } else {
                    const influence = clamp01((b.time - g.time) / D);
                    patchKeyframes((k) =>
                      k.id === b.id ? setIncomingHandle(a, b, influence, g.speed) : k
                    );
                  }
                  return;
                }

                const m = marqueeRef.current;
                if (m) {
                  const pt = canvasPoint(e.clientX, e.clientY);
                  if (Math.hypot(pt.x - m.x0, pt.y - m.y0) > 3) m.moved = true;
                  m.x1 = pt.x;
                  m.y1 = pt.y;
                  setMarquee({ x0: m.x0, y0: m.y0, x1: m.x1, y1: m.y1 });
                  const boxed = keysInMarquee(m);
                  setSelectedIds(
                    m.additive
                      ? [...new Set([...m.baseIds, ...boxed])]
                      : boxed
                  );
                  return;
                }

                const d = dragRef.current;
                if (!d || !clip) return;
                const L = layoutRef.current;
                const plotW = L.w - L.padL - L.padR;
                const plotH = L.h - L.padT - L.padB;
                const dt = ((e.clientX - d.startX) / plotW) * (L.t1 - L.t0);
                const ds = -((e.clientY - d.startY) / plotH) * (L.s1 - L.s0);

                const byId = new Map(d.origins.map((o) => [o.id, o]));
                patchKeyframes((k) => {
                  const o = byId.get(k.id);
                  if (!o) return k;
                  let next: Keyframe = {
                    ...k,
                    time: Math.max(0, o.time + dt),
                  };
                  const idx = kfsRef.current.findIndex((x) => x.id === k.id);
                  const spd = Math.max(0, o.speed + ds);
                  if (idx >= 0 && idx < kfsRef.current.length - 1) {
                    const b = kfsRef.current[idx + 1]!;
                    next = setOutgoingAbsSpeed(next, b, spd);
                  } else if (idx > 0) {
                    const a = kfsRef.current[idx - 1]!;
                    next = setIncomingAbsSpeed(a, next, spd);
                  }
                  return next;
                });
              }}
              onPointerUp={() => {
                const m = marqueeRef.current;
                if (m && !m.moved && !m.additive) {
                  setSelectedIds([]);
                }
                marqueeRef.current = null;
                setMarquee(null);
                dragRef.current = null;
                tangentDragRef.current = null;
                scrubRef.current = false;
              }}
              onPointerCancel={() => {
                marqueeRef.current = null;
                setMarquee(null);
                dragRef.current = null;
                tangentDragRef.current = null;
                scrubRef.current = false;
              }}
              onPointerMoveCapture={(e) => {
                // Cursor affordance over playhead when idle
                if (
                  scrubRef.current ||
                  dragRef.current ||
                  tangentDragRef.current ||
                  marqueeRef.current
                ) {
                  return;
                }
                const canvas = e.currentTarget;
                if (hitTestPlayhead(e.clientX, e.clientY)) {
                  canvas.style.cursor = 'ew-resize';
                } else if (hitTestKey(e.clientX, e.clientY) || hitTestTangent(e.clientX, e.clientY)) {
                  canvas.style.cursor = 'pointer';
                } else {
                  canvas.style.cursor = 'crosshair';
                }
              }}
            />
          </div>
          <div className="flex shrink-0 items-center justify-between gap-2 border-t border-hairline px-2 py-1 text-[10px] text-text-low">
            <span className="min-w-0 truncate">
              {kfs.length === 0
                ? `No ${property} keys — add with ◇ in Transform`
                : kfs.length < 2
                  ? `1 key · drag empty area to marquee · grab playhead to scrub`
                  : `Drag empty = marquee · grab playhead to scrub · Alt-wheel zoom`}
            </span>
            <div className="flex shrink-0 items-center gap-2">
              {selectedIds.length > 0 && (
                <span className="text-accent">{selectedIds.length} selected</span>
              )}
              <button
                type="button"
                title="Scale view to fit keyframes and playhead"
                className="border border-hairline px-1.5 py-0.5 text-[10px] text-text-high hover:border-accent hover:text-accent"
                onClick={() => fitToKeys()}
              >
                Fit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function clamp01(n: number) {
  return Math.min(0.98, Math.max(0.02, n));
}
